/**
 * StandaloneBootTracker
 *
 * Fires a single ``install_completed`` funnel event the first time the app
 * boots in PWA standalone mode on a given device. This is the only way to
 * count iOS installs — Safari never fires ``appinstalled``, so without this
 * hook iOS conversion shows as 0 forever.
 *
 * De-duped per device via localStorage (``zcardon_install_reported``) so
 * launching the app every day doesn't inflate the funnel.
 *
 * Renders nothing.
 */
import { useEffect } from "react";

const REPORTED_KEY = "zcardon_install_reported";

function detectPlatform() {
  const ua = (navigator.userAgent || "").toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return "ios";
  if (/android/.test(ua)) return "android";
  return "desktop";
}

function isStandalone() {
  return (
    Boolean(window.navigator.standalone) ||
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.matchMedia?.("(display-mode: fullscreen)").matches
  );
}

export default function StandaloneBootTracker() {
  useEffect(() => {
    if (!isStandalone()) return;
    if (localStorage.getItem(REPORTED_KEY) === "1") return;

    const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/public/install-event`;
    const payload = {
      kind: "install_completed",
      platform: detectPlatform(),
      source: "standalone-boot",
      session_id: localStorage.getItem("zcardon_session_id") || "",
    };

    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true, // survive a follow-up navigation
    })
      .then((r) => {
        if (r.ok) localStorage.setItem(REPORTED_KEY, "1");
      })
      .catch(() => {
        // Silent — funnel is best-effort, never blocks the app.
      });
  }, []);

  return null;
}
